import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiClient from '../../methods/api/apiClient';
import loader from '../../methods/loader';
import { Link } from 'react-router-dom';
import './style.scss';
import AuthLayout from '../../components/AuthLayout';
import methodModel from '../../methods/methods';
import { useDispatch, useSelector } from 'react-redux';
import { login_success } from '../actions/user';
import { toast } from 'react-toastify';
import { useSignIn, useUser, useSession, useClerk } from '@clerk/clerk-react';

const clerkAvailable = !!process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

const SsoButtons = ({ onSsoComplete }: { onSsoComplete: (data: any) => void }) => {
  const { signIn } = useSignIn();
  const { user: clerkUser, isSignedIn } = useUser();
  const { session } = useSession();
  const clerk = useClerk();
  const [ssoLoading, setSsoLoading] = useState('');

  useEffect(() => {
    if (window.location.hash === '#/continue') {
      window.history.replaceState(null, '', '/login');
    }
    if (!isSignedIn) {
      const sessionId = clerk.client?.signUp?.createdSessionId || clerk.client?.signIn?.createdSessionId;
      if (sessionId) {
        clerk.setActive({ session: sessionId }).catch(() => {});
      }
    }
  }, []);

  useEffect(() => {
    if (!isSignedIn || !clerkUser || !session) return;
    // If the app just logged the user out for a 12h session expiry, do NOT silently re-login them
    // through the still-valid Clerk session — sign Clerk out and require a fresh sign-in. This is
    // what makes the 12h floor actually stick for SSO users.
    if (localStorage.getItem('sessionExpired')) {
      localStorage.removeItem('sessionExpired');
      clerk.signOut().catch(() => {});
      return;
    }
    (async () => {
      try {
        const token = await session.getToken();
        const res = await new Promise<any>((resolve) => {
          ApiClient.post('api/user/sso-login', { clerkSessionToken: token }).then(resolve);
        });
        if (res.success && res.data) {
          onSsoComplete(res.data);
        } else {
          toast.error('Account with this email not found');
          await clerk.signOut();
          setSsoLoading('');
        }
      } catch {
        toast.error('Account with this email not found');
        await clerk.signOut().catch(() => {});
        setSsoLoading('');
      }
    })();
  }, [isSignedIn, clerkUser, session]);

  const handleSso = async (strategy: 'oauth_google' | 'oauth_microsoft') => {
    if (!signIn) return;
    if (isSignedIn) return;
    setSsoLoading(strategy);
    try {
      await signIn.authenticateWithRedirect({
        strategy,
        redirectUrl: '/sso-callback',
        redirectUrlComplete: '/login',
      });
    } catch (err: any) {
      toast.error(err.message || 'SSO failed');
      setSsoLoading('');
    }
  };

  return (
    <div className="space-y-3">
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
        <div className="relative flex justify-center text-sm"><span className="px-3 bg-white text-gray-400">or continue with</span></div>
      </div>
      <button
        type="button"
        onClick={() => handleSso('oauth_google')}
        disabled={!!ssoLoading}
        className="w-full flex items-center justify-center gap-3 h-11 rounded-lg border border-gray-300 hover:bg-gray-50 transition-all text-sm font-medium text-gray-700 disabled:opacity-50"
      >
        <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
        {ssoLoading === 'oauth_google' ? 'Signing in...' : 'Sign in with Google'}
      </button>
      <button
        type="button"
        onClick={() => handleSso('oauth_microsoft')}
        disabled={!!ssoLoading}
        className="w-full flex items-center justify-center gap-3 h-11 rounded-lg border border-gray-300 hover:bg-gray-50 transition-all text-sm font-medium text-gray-700 disabled:opacity-50"
      >
        <svg width="18" height="18" viewBox="0 0 21 21"><rect x="1" y="1" width="9" height="9" fill="#f25022"/><rect x="11" y="1" width="9" height="9" fill="#7fba00"/><rect x="1" y="11" width="9" height="9" fill="#00a4ef"/><rect x="11" y="11" width="9" height="9" fill="#ffb900"/></svg>
        {ssoLoading === 'oauth_microsoft' ? 'Signing in...' : 'Sign in with Microsoft 365'}
      </button>
    </div>
  );
};

const Login = () => {
  const history = useNavigate();
  const user = useSelector((state: any) => state.user);
  const dispatch = useDispatch();
  useEffect(() => {
    if (user && user?.loggedIn) {
      const redirectParam = methodModel.getPrams('redirect');
      if (redirectParam) {
        history(redirectParam);
        return;
      }
      const eventId = methodModel.getPrams('eventId');
      const checkinParam = methodModel.getPrams('checkin');
      if (eventId && checkinParam) {
        history(`/checkin/${eventId}`);
      } else {
        history('/dashboard');
      }
    }
  }, [])

  const [ip, setIp] = useState('');
  const [username, setUsername] = useState('');
  const [attandanceEmail, setAttendanceEmail] = useState('');
  const [remember, setRemember] = useState(false);
  const [password, setPassword] = useState('');
  const [eyes, setEyes] = useState({ password: false, confirmPassword: false, currentPassword: false });
  const [step, setStep] = useState(1);
  const [otp, setOTP] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [resp, setRes]: any = useState();
  useEffect(() => {
    // Redirect if already logged in
    if (user && user?.loggedIn) {
      const redirectParam = methodModel.getPrams('redirect');
      if (redirectParam) {
        history(redirectParam);
        return;
      }
      const eventId = methodModel.getPrams('eventId');
      const checkinParam = methodModel.getPrams('checkin');
      if (eventId && checkinParam) {
        history(`/checkin/${eventId}`);
      } else {
        history('/dashboard');
      }
      return;
    }

    // Prefill remembered login (if user chose Remember)
    const r = localStorage.getItem('remember')
    if (r) {
      try {
        const data = JSON.parse(r)
        setUsername(data.email || "")
        setPassword(data.password || "")
        setRemember(true)
      } catch (e) {
        // ignore parse errors
      }
    }
  
    // Get IP address (best-effort)
    fetch('https://api.ipify.org?format=json')
      .then(response => response.json())
      .then(data => {
        const ip = data.ip
        localStorage.setItem('IP', ip)
        setIp(ip)
      })
      .catch(error => console.error('Error fetching IP address:', error));
  
    // Only prefill username from query 'email' when NOT an RSVP flow
    // Identify RSVP by presence of eventId + status (or attended/attendance param)
    const emailParam = methodModel.getPrams('email')
    const eventIdParam = methodModel.getPrams('eventId')
    const statusParam = methodModel.getPrams('status') || methodModel.getPrams('attended') || methodModel.getPrams('attendance')
  
    if (emailParam && !eventIdParam && !statusParam) {
      setUsername(emailParam)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  
  let attendance = methodModel.getPrams('attendance')

  // const setLogin = async (data: any) => {
  //   let url = '/profile'
  //   let eventId = methodModel.getPrams('eventId')

  //   if (eventId) {
  //     if (methodModel.getPrams('attended')) {
  //       try {
  //         const res = await ApiClient.get(`api/attandance?email=${data?.email}&eventId=${eventId}`);
        
  //         if (res.success === true) {
  //           url = `/thanku`
  //         }
  //       } catch (error) {
  //         console.error("Error fetching attendance:", error);
  //       }
  //     } else {
  //       url = `/event/detail/${eventId}`
  //     }
  //   }

  //   localStorage.setItem('token', data?.access_token )

  //   dispatch(login_success(data));

  //     history(url);
    

  // }

  const setLogin = async (data: any) => {
    // Get token from data OR from temp storage (for OTP flow)
    const token = data?.access_token || localStorage.getItem('temp_token');
    
    if (token) {
        localStorage.setItem('token', token);
        localStorage.removeItem('temp_token');
    }

    // Start the 12h session clock for EVERY login path — including SSO/Clerk, which previously
    // skipped this (only the password/OTP flow set it), so the 12h expiry never applied to SSO users.
    localStorage.setItem('loginTime', new Date().toISOString());
    localStorage.removeItem('sessionExpired');

    dispatch(login_success(data));

    // Check for redirect param (e.g., from RSVP "Check Event Details")
    const redirectParam = methodModel.getPrams('redirect');
    if (redirectParam) {
      history(redirectParam);
      return;
    }

    // Determine if login was invoked from an RSVP link
    const eventId = methodModel.getPrams('eventId')
    const status = methodModel.getPrams('status') // prefer 'status' (Yes/No)
    const attended = methodModel.getPrams('attended') // older param name we may receive
    const attendance = methodModel.getPrams('attendance') // other older param name
    const attendeeId = methodModel.getPrams('attendeeId')
  
    const rsvpStatus = status || attended || attendance
  
    if (eventId && rsvpStatus) {
      // Redirect to RSVP confirmation page (do NOT go directly to event detail)
      const q = new URLSearchParams({
        eventId,
        status: rsvpStatus.toString(),
        ...(attendeeId ? { attendeeId } : {})
      }).toString()
      history(`/rsvp-confirmation?${q}`)
      return
    }
  
    // Non-RSVP normal flow: preserve existing logic (go to profile/dashboard/event etc)
    // Preserve any existing checkin or attended flow if used elsewhere
    let url = '/profile'
    const checkinParam = methodModel.getPrams('checkin')
    const attendedParam = methodModel.getPrams('attended')
  
    if (eventId) {
      if (checkinParam) {
        url = `/checkin/${eventId}`
      } else if (attendedParam) {
        try {
          const res = await ApiClient.get(`api/attandance?email=${data?.email}&eventId=${eventId}`);
          if (res.success === true) {
            url = `/thanku`
          }
        } catch (err) {
          // ignore
        }
      } else {
        url = `/event/detail/${eventId}`
      }
    }
  
    history(url)
  }
  
    
  let userId = localStorage.getItem("id")
 
const resendOtp =()=>{
 let url  = 'api/resendOtp' ,
  data: any = {
 id:userId
};
  ApiClient.post(url, data)
  .then((res) => {
  
   toast(res?.message)
  })

}


const hendleSubmit = (e: any) => {
  e.preventDefault()
  let data: any = {
    email: username,  // cw: Use email for login
    password,
    ip_address: ip,
    trustedDeviceToken: localStorage.getItem('trustedDeviceToken') || ''
  };

  let url = 'api/user/login'
  if (step == 2) {
    url = 'api/two-factor/auth'
    data = {
      id: resp?._id,
      otp: otp,
      ip_address: ip,
      trustDevice: trustDevice
    }
  }
  if (step == 3) {
    url = 'api/2fa/verify-login'
    data = {
      tempToken: tempToken,
      code: totpCode
    }
  }

  loader(true)
  ApiClient.post(url, data).then(async res => {
    if (res.success == true) {
      localStorage.setItem('id', res?.data?._id)
      if (step == 1 && res?.data?.access_token) {
        localStorage.setItem('temp_token', res.data.access_token);
      }
      // cw: store trusted device token if returned
      if (res.data?.trustedDeviceToken) {
        localStorage.setItem('trustedDeviceToken', res.data.trustedDeviceToken);
      }
      if (remember) {
        localStorage.setItem('remember', JSON.stringify({ email: username, password }))
      } else {
        localStorage.removeItem('remember')
      }
      if (step == 1 && res.data?.requires2FA && res.data?.tempToken) {
        setTempToken(res.data.tempToken);
        if (res.data.needsSetup) {
          setQrCode(res.data.qrCode || '');
          setTotpSecret(res.data.secret || '');
        }
        setStep(3);
      } else if (step == 1 && res.data.two_factor_email_sent) {
        setRes(res.data);
        setStep(2);
      } else {
        setLogin(res.data);
      }
      const loginTime = new Date();
      localStorage.setItem('loginTime', loginTime.toISOString());
    } else {
      // cw: Show toast error for failed login
      if (step == 1) {
        toast.error(res.message || res.error?.message || "Invalid Login ID or Password");
      } else if (step == 3) {
        toast.error(res.message || res.error?.message || "Invalid authenticator code");
      } else {
        toast.error(res.message || res.error?.message || "Invalid OTP");
      }
    }
    loader(false)
  }).catch((error) => {
    // cw: Handle network or unexpected errors
    loader(false)
    toast.error(error?.response?.data?.message || "Login failed. Please try again.");
  });
};
  const handleAttendence = (e: any) => {
    e.preventDefault()
    let eventId = methodModel.getPrams('eventId')
    loader(true)
    ApiClient.post('api/find/user', { email: attandanceEmail }, '', true).then((res2: any) => {
      if (res2.success) {
        let url = `/login?eventId=${eventId}&email=${attandanceEmail}&attended=true`
        loader(false)
        history(url);
      } else {
        ApiClient.get(`api/get/login-id?email=${attandanceEmail}`)
          .then((res: any) => {
            loader(false)
            let url = `/signup?eventId=${eventId}&email=${attandanceEmail}&attended=true`
            history(url);
          })
          .catch(() => {
            loader(false)
            let url = `/signup?eventId=${eventId}&email=${attandanceEmail}&attended=true`
            history(url);
          });
      }
    }).catch(() => {
      loader(false)
      toast.error("Something went wrong. Please try again.");
    });
  };
  const [emailError, setEmailError] = useState('');

  const validateEmail = (email :any) => {
    // Email regex pattern for basic validation
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email);
  };

  const handleEmailChange = (e :any) => {
    const email = e.target.value;
    setAttendanceEmail(email);

    if (email.trim() === '') {
      setEmailError('Email address is required');
    } else if (!validateEmail(email)) {
      setEmailError('Invalid email format');
    } else {
      setEmailError('');
    }
  };
  return (
    <>
      <AuthLayout>
        {attendance === "true" ? <form onSubmit={handleAttendence}>
          <div>
            {/* <input
              type="email"
              className="shadow-box bg-white mb-6 w-full text-sm placeholder:text-gray-500 rounded-lg h-10 flex items-center gap-2 overflow-hidden px-2 !ring-primary !outline-primary"
              placeholder="Email address"
              value={attandanceEmail}
              onChange={(e) => setAttendanceEmail(e.target.value)}
              required
            /> */}
             <input
        type="email"
        className={`shadow-box bg-white mb-6 w-full text-sm placeholder:text-gray-500 rounded-lg h-10 flex items-center gap-2 overflow-hidden px-2 ${
          emailError ? 'border-red-500' : '' // Add red border if there's an error
        }`}
        placeholder="Email address"
        value={attandanceEmail}
        onChange={handleEmailChange}
        required
      />
      {emailError && <p className="text-red-500 text-xs">{emailError}</p>}
          </div>
          <div className="mt-8">
            {/* <label><input type="checkbox" checked={remember} onChange={(e)=>setRemember(e.target.checked)} className="mr-2" /> Remember Me</label> */}
            <button
              type="submit"
              className="!px-4 w-full text-sm font-normal text-white h-11 flex items-center justify-center gap-2 !bg-orange-500 rounded-lg shadow-btn hover:opacity-80 transition-all focus:ring-2 ring-[#EDEBFC] disabled:bg-[#D0CAF6] disabled:cursor-not-allowed">
              Mark Attendence
            </button>
          </div>
        </form> :
          <form onSubmit={hendleSubmit}>
            <h4 className="text-typo mb-6 text-2xl font-medium">
              Sign in
            </h4>
            {step == 1 ? <>
              <input
                type="text"
                className="shadow-box bg-white mb-6 w-full text-sm placeholder:text-gray-500 rounded-lg h-10 flex items-center gap-2 overflow-hidden px-2 !ring-primary !outline-primary"
                placeholder="Email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                // disabled={methodModel.getPrams('attended')?true:false}
                required
              />
              <div className="relative mb-6">
                <input
                  type={eyes.password ? "text" : "password"}
                  className="shadow-box bg-white w-full text-sm placeholder:text-gray-500 rounded-lg h-10 flex items-center gap-2 overflow-hidden px-2 !ring-primary !outline-primary"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                />
                {eyes.password ? (
                  <i
                    className="fa fa-eye top-3 right-3 absolute text-gray-600"
                    onClick={() =>
                      setEyes({ ...eyes, password: !eyes.password })
                    }
                  />
                ) : (
                  <i
                    className="fa fa-eye-slash top-3 right-3 absolute text-gray-600"
                    onClick={() =>
                      setEyes({ ...eyes, password: !eyes.password })
                    }
                  />
                )}
              </div>
            </> : step == 3 ? <>

              {qrCode || totpSecret ? <>
                <p className="mb-2 text-sm text-gray-600">Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)</p>
                {qrCode && (
                  <div className="flex justify-center mb-4">
                    <img src={qrCode} alt="QR Code" className="w-40 h-40 border rounded-lg" />
                  </div>
                )}
                {totpSecret && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-400 mb-1">Can't scan? Enter this key manually:</p>
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded select-all break-all">{totpSecret}</code>
                  </div>
                )}
                <p className="mb-2 text-sm">Then enter the 6-digit code it shows</p>
              </> : <p className="mb-2">Enter the 6-digit code from your authenticator app</p>}
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                minLength={6}
                className="shadow-box bg-white mb-6 w-full text-sm placeholder:text-gray-500 rounded-lg h-10 flex items-center gap-2 overflow-hidden px-2 !ring-primary !outline-primary"
                placeholder="000000"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
              />

            </> : <>

              <p className="mb-2">OTP sent on email</p>
              <input
                type="text"
                maxLength={6}
                minLength={6}
                className="shadow-box bg-white mb-6 w-full text-sm placeholder:text-gray-500 rounded-lg h-10 flex items-center gap-2 overflow-hidden px-2 !ring-primary !outline-primary"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => setOTP(e.target.value)}
                required
              />


            </>}

            <div className='flex'>
          
            {step == 1 ? <>
              <label className='flex items-center pointer'><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="mr-2 h-4 w-4" /> <span className='text-md text-gray-600'>Remember Me</span></label>
              <Link className="sign_up ml-auto text-orange-500" to="/forgotpassword"> Forgot Password</Link>
            </> : step == 2 ? <div className="flex flex-col gap-2 w-full">
              <label className='flex items-center pointer'><input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} className="mr-2 h-4 w-4" /> <span className='text-sm text-gray-600'>Skip OTP on this device for 30 days</span></label>
              <p className="sign_up text-orange-500 cursor-pointer text-right" onClick={resendOtp}>Resend OTP</p>
            </div> : null}
            </div>
            <div className="mt-8">
              {/* <label><input type="checkbox" checked={remember} onChange={(e)=>setRemember(e.target.checked)} className="mr-2" /> Remember Me</label> */}
              <button
                type="submit"
                className="!px-4 w-full text-sm font-normal text-white h-11 flex items-center justify-center gap-2 !bg-orange-500 rounded-lg shadow-btn hover:opacity-80 transition-all focus:ring-2 ring-[#EDEBFC] disabled:bg-[#D0CAF6] disabled:cursor-not-allowed">
                Sign in
              </button>
            </div>
            <p className='text-sm mt-3 text-center'>Don't have an account? <Link to="/signup" className='text-orange-500 text-sm'>Sign Up</Link></p>
            {clerkAvailable && step === 1 && (
              <SsoButtons onSsoComplete={(data: any) => setLogin(data)} />
            )}
          </form>}



      </AuthLayout>
    </>
  );
};

export default Login;